/**
 * SHA-256, vendored (WS-17c). ~70 lines of FIPS 180-4, no dependency, **synchronous**.
 *
 * Why not `crypto.subtle.digest`? Two reasons, in order of importance:
 *
 *  1. **The engine must stay pure and synchronous.** `deriveBattleship` verifies a
 *     commit–reveal pair as part of deriving state from a move log, and `replayStates`
 *     calls `derive` once per prefix. An `async` derive would infect the replay engine, the
 *     React render path, and every test in this module. A 70-line hash keeps `derive` what
 *     `deriveConnect4` already is: `(moves) => state`, no clock, no promises, no I/O.
 *  2. **`crypto.subtle` is not always there.** It requires a secure context; a household hub
 *     reached over plain http on the LAN (a real way this app gets used) has
 *     `window.crypto.subtle === undefined`, and a fair-play check that silently stops
 *     working on half the devices is worse than no fair-play check at all.
 *
 * Vendoring rather than adding a dependency is the standing rule here
 * (`memory/no-big-players-offline-first.md`, north-star #7). This is a *fairness* hash, not
 * a password hash or a signature: the threat model is a sibling who reloads the page and
 * drags a ship, not an adversary with a GPU farm. Do not reach for this for anything where
 * constant-time comparison or key derivation matters — `apps/server/src/modules/auth` owns
 * that and uses real primitives.
 */
/** First 32 bits of the fractional parts of the cube roots of the first 64 primes. */
const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
/** First 32 bits of the fractional parts of the square roots of the first 8 primes. */
const H0 = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
]);
function rotr(x, n) {
    return (x >>> n) | (x << (32 - n));
}
/** UTF-8 encodes without `TextEncoder`, which older WebViews behind a household hub may lack. */
export function utf8Bytes(text) {
    const out = [];
    for (let i = 0; i < text.length; i += 1) {
        let code = text.charCodeAt(i);
        if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
            const next = text.charCodeAt(i + 1);
            if (next >= 0xdc00 && next <= 0xdfff) {
                code = 0x10000 + ((code - 0xd800) << 10) + (next - 0xdc00);
                i += 1;
            }
        }
        if (code < 0x80)
            out.push(code);
        else if (code < 0x800)
            out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
        else if (code < 0x10000)
            out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
        else
            out.push(0xf0 | (code >> 18), 0x80 | ((code >> 12) & 0x3f), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    }
    return Uint8Array.from(out);
}
/** SHA-256 of `text`'s UTF-8 bytes, lowercase hex. Deterministic everywhere, sync. */
export function sha256Hex(text) {
    const bytes = utf8Bytes(text);
    const bitLength = bytes.length * 8;
    // message + 0x80 + zero padding to 56 mod 64 + 8-byte big-endian length
    const paddedLength = (((bytes.length + 8) >> 6) + 1) << 6;
    const padded = new Uint8Array(paddedLength);
    padded.set(bytes);
    padded[bytes.length] = 0x80;
    const view = new DataView(padded.buffer);
    // JS bit ops are 32-bit; a household game log will never reach 2^32 bits, so the high word
    // is written via floating-point division rather than a shift that would silently wrap.
    view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000));
    view.setUint32(paddedLength - 4, bitLength >>> 0);
    const h = new Uint32Array(H0);
    const w = new Uint32Array(64);
    for (let offset = 0; offset < paddedLength; offset += 64) {
        for (let i = 0; i < 16; i += 1)
            w[i] = view.getUint32(offset + i * 4);
        for (let i = 16; i < 64; i += 1) {
            const a = w[i - 15];
            const b = w[i - 2];
            const s0 = rotr(a, 7) ^ rotr(a, 18) ^ (a >>> 3);
            const s1 = rotr(b, 17) ^ rotr(b, 19) ^ (b >>> 10);
            w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
        }
        let a = h[0];
        let b = h[1];
        let c = h[2];
        let d = h[3];
        let e = h[4];
        let f = h[5];
        let g = h[6];
        let hh = h[7];
        for (let i = 0; i < 64; i += 1) {
            const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
            const ch = (e & f) ^ (~e & g);
            const t1 = (hh + s1 + ch + K[i] + w[i]) >>> 0;
            const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
            const maj = (a & b) ^ (a & c) ^ (b & c);
            const t2 = (s0 + maj) >>> 0;
            hh = g;
            g = f;
            f = e;
            e = (d + t1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (t1 + t2) >>> 0;
        }
        h[0] = (h[0] + a) >>> 0;
        h[1] = (h[1] + b) >>> 0;
        h[2] = (h[2] + c) >>> 0;
        h[3] = (h[3] + d) >>> 0;
        h[4] = (h[4] + e) >>> 0;
        h[5] = (h[5] + f) >>> 0;
        h[6] = (h[6] + g) >>> 0;
        h[7] = (h[7] + hh) >>> 0;
    }
    let hex = '';
    for (let i = 0; i < 8; i += 1)
        hex += h[i].toString(16).padStart(8, '0');
    return hex;
}
/** Shape a commit/reveal hash must have before the engine will even look at it. */
export function isSha256Hex(value) {
    return typeof value === 'string' && /^[0-9a-f]{64}$/.test(value);
}
//# sourceMappingURL=sha256.js.map